import { useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { useInitializationDataContext } from '../../../context/data-context/data-context';
import {
  AddressForm as AddressFormData,
  AddressTypes,
  EmailAddressForm as EmailAddressFormData,
} from '../../../global-types';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { getRegionalConfig } from '../../../regional-config';
import { useAddressStore } from '../../../stores/address';
import { useEditorNavigation } from '../../editor/hooks/use-editor-navigation';

export const useAddressGoBack = ({ isDigitalGreeting, envelope }) => {
  // Contexts
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const { addressData, isRecipientAddress, setIsRecipientAddress, updateDigitalAddress, setAddressData } =
    useAddressStore(
      useShallow((store) => ({
        addressData: store.addressData,
        isRecipientAddress: store.isRecipientAddress,
        setIsRecipientAddress: store.setIsRecipientAddress,
        updateDigitalAddress: store.updateDigitalAddress,
        setAddressData: store.setAddressData,
      })),
    );

  const {
    addressForm: { shouldShowOneToManyEnvelopePreview },
  } = getRegionalConfig();
  const { trackReturnToEdit } = useAnalyticsContext();

  const isOneToMany = useIsOneToMany();
  const { IS_EDITOR_HEADER_ENABLED } = useFeatureFlags();
  const { handleIndexChange } = useEditorNavigation();

  // Routing
  const navigate = useNavigate();
  const { search } = useLocation();

  const goBack = (addressFormValues: AddressFormData, emailAddressFormValues: EmailAddressFormData) => {
    if (isDigitalGreeting) {
      const currentEmailAddress = { ...addressData.digital, ...emailAddressFormValues };
      updateDigitalAddress(currentEmailAddress);
    } else {
      const currentAddressType = isRecipientAddress ? AddressTypes.RECIPIENT : AddressTypes.SENDER;
      const currentAddress = { ...addressData[String(currentAddressType)], ...addressFormValues };

      if (!isOneToMany) {
        setAddressData({ [currentAddressType]: currentAddress });
      }
    }

    if (isRecipientAddress) {
      setIsRecipientAddress(false);
      return;
    }

    trackReturnToEdit();
    const searchParams = new URLSearchParams(search);

    if (shouldShowOneToManyEnvelopePreview && envelope) {
      searchParams.set('envelope', envelope);
    }

    const lastProjectLoaded = sessionStorage.getItem('lastProjectLoaded');
    const editorUrl = lastProjectLoaded
      ? `/card/customization/edit/${lastProjectLoaded}`
      : `/card/customization/edit/${initializedData?.project_id}`;

    navigate({ pathname: editorUrl, search: searchParams.toString() });
    if (!IS_EDITOR_HEADER_ENABLED) {
      handleIndexChange(0, 0);
    }
  };

  return {
    goBack,
  };
};
